<script lang="ts">
  // Continuity module, Phase 1: the OKR register (on-screen term for what
  // the backend still calls "critical jobs" — see internal/models/models.go
  // and DESIGN). Admin-only; the server enforces this too (RequireAdmin on
  // every /api/admin/okrs route), this page is just the surface.
  import { untrack } from 'svelte';
  import { createQuery, useQueryClient } from '@tanstack/svelte-query';
  import { Plus } from 'lucide-svelte';
  import { okrsApi } from '$lib/api/okrs';
  import { getErrorMessage } from '$lib/api/client';
  import { toast } from '$lib/state/toast.svelte';
  import Modal from '$lib/components/ui/Modal.svelte';
  import StatusLamp from '$lib/components/ui/StatusLamp.svelte';
  import InfoTip from '$lib/components/ui/InfoTip.svelte';
  import OKRDetail from './OKRDetail.svelte';
  import type { OKRColour } from '$lib/types';

  const CRITICAL_JOB_TOOLTIP =
    'One specific outcome the company needs someone to be able to deliver — like releasing Gerbers to the fab, or negotiating with a vendor. Not a job title. If one person leaving would stop it, it belongs here.';

  const LAMP_TONE: Record<OKRColour, 'bad' | 'warn' | 'good'> = { red: 'bad', amber: 'warn', green: 'good' };

  const TIME_TO_REPLACE_LABEL: Record<string, string> = {
    under_1m: 'Under 1 month',
    '1_3m': '1–3 months',
    '3_6m': '3–6 months',
    '6_12m': '6–12 months',
    over_12m: 'Over 12 months'
  };

  // jumpToId lets a sibling view (the People detail's "OKRs they do/back
  // up/can also do" links, see continuityNav.svelte.ts) open a specific OKR's
  // detail directly instead of landing on the list. initialFilter is the
  // Continuity Home cards' "click through" (Phase 4) — a one-shot quick
  // filter, cleared locally once the admin dismisses it.
  const { jumpToId = null, initialFilter = null }: { jumpToId?: number | null; initialFilter?: 'red' | 'never_proven' | null } =
    $props();

  const queryClient = useQueryClient();
  const query = createQuery(() => ({
    queryKey: ['okrs'],
    queryFn: () => okrsApi.list()
  }));

  function refresh() {
    queryClient.invalidateQueries({ queryKey: ['okrs'] });
  }

  let selectedId = $state<number | null>(null);
  let groupFilter = $state('');
  let quickFilter = $state<'red' | 'never_proven' | null>(untrack(() => initialFilter));

  $effect(() => {
    if (jumpToId != null) selectedId = jumpToId;
  });

  const QUICK_FILTER_LABEL = { red: 'Only one person can do this', never_proven: 'Never proven' };

  const groups = $derived(
    query.data ? [...new Set(query.data.map((r) => r.group).filter(Boolean))].sort() : []
  );
  const filteredRows = $derived(
    (query.data || [])
      .filter((r) => (groupFilter ? r.group === groupFilter : true))
      .filter((r) => (quickFilter === 'red' ? r.colour === 'red' : quickFilter === 'never_proven' ? r.last_proven === null : true))
  );

  let showCreate = $state(false);
  let createName = $state('');
  let createGroup = $state('');
  let createError = $state('');
  let creating = $state(false);

  function openCreate() {
    createName = '';
    createGroup = '';
    createError = '';
    showCreate = true;
  }

  async function createOKR() {
    if (!createName.trim()) {
      createError = 'Name is required.';
      return;
    }
    creating = true;
    createError = '';
    try {
      const okr = await okrsApi.create({ name: createName, group: createGroup });
      refresh();
      showCreate = false;
      toast.success('OKR added.');
      selectedId = okr.id;
    } catch (err) {
      createError = getErrorMessage(err);
    } finally {
      creating = false;
    }
  }

  // Part B: the one-time starter-register load, only offered while the
  // list is genuinely empty — the server also refuses (409) once anything
  // exists, this is just the UI not offering a button that would fail.
  let importing = $state(false);

  // Two-step inline confirm instead of native confirm() — a native dialog
  // freezes the renderer for CDP/browser automation (this cost a prod
  // click once already). First click arms it (auto-reverts after 4s or on
  // blur); second click while armed actually runs it. See
  // PersonDetail.svelte's deleteCustody/cancelNotice for the same pattern.
  let confirmingImport = $state(false);
  let confirmImportTimer: ReturnType<typeof setTimeout> | undefined;

  function armImportConfirm() {
    confirmingImport = true;
    clearTimeout(confirmImportTimer);
    confirmImportTimer = setTimeout(() => (confirmingImport = false), 4000);
  }

  async function loadStarterRegister() {
    if (!confirmingImport) {
      armImportConfirm();
      return;
    }
    confirmingImport = false;
    clearTimeout(confirmImportTimer);
    importing = true;
    try {
      const result = await okrsApi.importInitial();
      refresh();
      const unmatchedNote = result.unmatched.length ? ` ${result.unmatched.length} name(s) unmatched: ${result.unmatched.join(', ')}.` : '';
      toast.success(`Loaded ${result.created} OKRs.${unmatchedNote}`);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      importing = false;
    }
  }
</script>

{#if selectedId !== null}
  <OKRDetail id={selectedId} onBack={() => (selectedId = null)} onChanged={refresh} />
{:else}
  <div class="tym-page">
    <div class="mb-4 flex flex-wrap items-center justify-between gap-3">
      <p class="tym-section-sub flex items-center gap-1.5">
        Who can do what, and how exposed we are if they leave.
        <InfoTip text={CRITICAL_JOB_TOOLTIP} />
      </p>
      <div class="flex items-center gap-2">
        {#if quickFilter}
          <span class="tym-badge tym-badge--info">
            {QUICK_FILTER_LABEL[quickFilter]}
            <button type="button" class="ml-1 font-bold" onclick={() => (quickFilter = null)} aria-label="Clear filter">×</button>
          </span>
        {/if}
        {#if groups.length > 0}
          <select class="input !w-auto" aria-label="Filter by group" bind:value={groupFilter}>
            <option value="">All groups</option>
            {#each groups as g (g)}
              <option value={g}>{g}</option>
            {/each}
          </select>
        {/if}
        <button type="button" class="btn-primary" onclick={openCreate}>
          <Plus class="h-4 w-4" /> Add OKR
        </button>
      </div>
    </div>

    {#if query.isLoading}
      <div class="tym-loading-state">Loading...</div>
    {:else if query.isError}
      <div class="tym-error-state">Could not load OKRs.</div>
    {:else if query.data}
      <div class="tym-table-wrap">
        <div class="tym-table-scroll">
          <table class="tym-table">
            <thead>
              <tr>
                <th></th>
                <th>Key result</th>
                <th>Group</th>
                <th>Who does it</th>
                <th>Backup</th>
                <th>People who can do this</th>
                <th>Last proven</th>
                <th>Time to replace</th>
              </tr>
            </thead>
            <tbody>
              {#each filteredRows as row (row.id)}
                <tr class="is-hoverable cursor-pointer" onclick={() => (selectedId = row.id)}>
                  <td><span title={row.status_reason}><StatusLamp tone={LAMP_TONE[row.colour]} label={row.status_reason} /></span></td>
                  <td class="font-medium text-navy">{row.name}</td>
                  <td class="muted">{row.group || '—'}</td>
                  <td class="muted">{row.main?.name || '—'}</td>
                  <td class="muted">{row.backups.map((b) => b.name).join(', ') || '—'}</td>
                  <td class="muted">{row.people_who_can_do_this}</td>
                  <td class="muted">{row.last_proven || 'Never'}</td>
                  <td class="muted">{TIME_TO_REPLACE_LABEL[row.time_to_replace] || '—'}</td>
                </tr>
              {:else}
                <tr>
                  <td class="empty" colspan="8">
                    <span class="tym-empty-title">No OKRs yet</span>
                    Add the first one to start tracking who can cover it, or load the starter register below.
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      </div>
      {#if query.data.length === 0}
        <div class="mt-3">
          <button
            type="button"
            class={confirmingImport ? 'btn-danger-solid' : 'btn-secondary'}
            disabled={importing}
            onclick={loadStarterRegister}
            onblur={() => (confirmingImport = false)}
          >
            {importing ? 'Loading...' : confirmingImport ? 'Click again to confirm' : 'Load starter register'}
          </button>
        </div>
      {/if}
    {/if}
  </div>

  {#if showCreate}
    <Modal title="Add OKR" onClose={() => (showCreate = false)}>
      <div class="space-y-4">
        <div>
          <div class="label">Name</div>
          <input type="text" class="input" aria-label="Name" bind:value={createName} />
        </div>
        <div>
          <div class="label">Group</div>
          <input type="text" class="input" aria-label="Group" bind:value={createGroup} />
        </div>
        {#if createError}<div class="tym-error-state">{createError}</div>{/if}
        <div class="flex justify-end gap-2 pt-1">
          <button type="button" class="btn-secondary" onclick={() => (showCreate = false)}>Cancel</button>
          <button type="button" class="btn-primary" disabled={creating} onclick={createOKR}>
            {creating ? 'Adding...' : 'Add'}
          </button>
        </div>
      </div>
    </Modal>
  {/if}
{/if}
